// src/components/OptionFields/OptionFieldChoiceModal.tsx
// Type 3 リストボックス用の選択肢モーダルコンポーネント

import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface OptionFieldChoiceModalProps {
  visible: boolean;
  title: string;
  options: string[];
  onSelect: (value: string) => void;
  onCancel: () => void;
  /** 親モーダルの zIndex より大きい値を指定すること（省略時は Ant Design デフォルト） */
  zIndex?: number;
}

/**
 * Type 3 リストボックス用の検索可能な選択肢モーダル
 */
export const OptionFieldChoiceModal: React.FC<OptionFieldChoiceModalProps> = ({
  visible,
  title,
  options,
  onSelect,
  onCancel,
  zIndex,
}) => {
  const [searchText, setSearchText] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);

  // 検索テキストが変更されたときのフィルタリング
  useEffect(() => {
    if (!searchText) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchText, options]);

  // モーダルが開かれたときの初期化
  useEffect(() => {
    if (visible) {
      setSearchText('');
      setFilteredOptions(options);
    }
  }, [visible, options]);

  const handleSelect = (value: string) => {
    onSelect(value);
    setSearchText('');
  };

  const handleClear = () => {
    onSelect('');
    setSearchText('');
  };

  return (
    <>
      {visible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: (zIndex ?? 1000) - 1 }} />
      )}
      <Modal
        title={title || '選択してください'}
        open={visible}
        onCancel={onCancel}
        zIndex={zIndex}
        mask={false}
        footer={[
        <Button key="clear" onClick={handleClear}>
          クリア
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          キャンセル
        </Button>
      ]}
      width={400}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          placeholder="検索..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        
        <div
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            padding: '8px 0'
          }}
        >
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <Text type="secondary">該当する項目がありません</Text>
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <Button
                key={index}
                type="text"
                block
                style={{
                  textAlign: 'left',
                  height: 'auto',
                  padding: '8px 16px',
                  borderRadius: 0
                }}
                onClick={() => handleSelect(option)}
              >
                {option}
              </Button>
            ))
          )}
        </div>
      </Space>
      </Modal>
    </>
  );
};

export default OptionFieldChoiceModal;